import React, { useMemo, useState, useEffect } from "react";
import {
  MessageSquareQuote, HelpCircle, AlertCircle, Compass, Shield, Wrench,
} from "lucide-react";

/**
 * Advisor Coach — single calm panel that replaces the old stack of
 * Conversation Assistant + Diagnostic Guidance + Operational Prep Strip.
 *
 * Tabs (shown only when they have content for the current state):
 *   • Say          — question.ask_prompt           (per-question)
 *   • Why          — question.why_it_matters       (per-question)
 *                    + diagnostic_guidance.process_explanation (journey-level)
 *   • Expectation  — question.expectation_prompt   (per-question)
 *                    + diagnostic_guidance.expectation_guidance (journey-level)
 *   • Next steps   — operational tag_labels + likely_outcomes + likely_next_steps
 *   • Safety       — diagnostic_guidance.si_process_guidance
 *
 * Empty tabs are HIDDEN (never greyed out). The most relevant tab is
 * auto-selected when the active question changes.
 *
 * Trust prompts stay inline under the active question card. They do NOT
 * appear here — single source of truth.
 *
 * Tone: operational, not corporate. Thin underline indicator; no heavy
 * tab chrome.
 */
export default function AdvisorCoach({ activeNode, opPrep, guidance }) {
  const ask = activeNode?.ask_prompt;
  const why = activeNode?.why_it_matters;
  const expectationQ = activeNode?.expectation_prompt;

  const processExplanation = guidance?.process_explanation;
  const expectationGuidance = guidance?.expectation_guidance;
  const siGuidance = guidance?.si_process_guidance;
  // Operational-boundary line for MOT-only journeys. When present, REPLACES
  // the SI guidance in the Safety tab and renames the tab to "Scope" so the
  // FOH never sees inspection wording on an MOT-only booking.
  const motScopeGuidance = guidance?.mot_scope_guidance;
  const scopeGuidance = motScopeGuidance || siGuidance;
  const scopeTabLabel = motScopeGuidance ? "Scope" : "Safety";
  const likelyOutcomes = guidance?.likely_outcomes || [];
  const likelyNextSteps = guidance?.likely_next_steps || [];
  const tagLabels = opPrep?.tag_labels || [];
  const riskLabels = opPrep?.risk_labels || [];

  // Build the tab definitions only for tabs that have content.
  const tabs = useMemo(() => {
    const t = [];
    if (ask) {
      t.push({
        key: "say",
        label: "Say",
        icon: <MessageSquareQuote className="w-3.5 h-3.5" />,
        tone: "slate",
      });
    }
    if (why || processExplanation) {
      t.push({
        key: "why",
        label: "Why",
        icon: <HelpCircle className="w-3.5 h-3.5" />,
        tone: "slate",
      });
    }
    if (expectationQ || expectationGuidance) {
      t.push({
        key: "expect",
        label: "Expect",
        icon: <AlertCircle className="w-3.5 h-3.5" />,
        tone: "amber",
      });
    }
    if (tagLabels.length || riskLabels.length || likelyOutcomes.length || likelyNextSteps.length) {
      t.push({
        key: "next",
        label: "Next",
        icon: <Compass className="w-3.5 h-3.5" />,
        tone: "teal",
      });
    }
    if (scopeGuidance) {
      t.push({
        key: "safety",
        label: scopeTabLabel,
        icon: <Shield className="w-3.5 h-3.5" />,
        tone: "blue",
      });
    }
    return t;
  }, [
    ask, why, expectationQ, processExplanation, expectationGuidance,
    scopeGuidance, scopeTabLabel, tagLabels.length, riskLabels.length,
    likelyOutcomes.length, likelyNextSteps.length,
  ]);

  // Auto-pick the most useful starting tab for the current question.
  // Priority: Say (always for the per-question script) → Why → Next → Safety.
  const defaultKey = tabs[0]?.key;
  const [activeKey, setActiveKey] = useState(defaultKey);

  // When the active question changes, re-anchor to "Say" if available, so
  // FOH always lands on the per-question prompt first.
  useEffect(() => {
    if (!tabs.length) return;
    const target = tabs.find((t) => t.key === "say") || tabs[0];
    setActiveKey(target.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeNode?.id, tabs.length]);

  if (!tabs.length) return null;

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
      data-testid="advisor-coach"
    >
      <header className="px-4 pt-3.5 pb-2 flex items-center gap-2 border-b border-slate-100">
        <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />
        <p className="text-[12.5px] uppercase tracking-[0.18em] text-slate-700 font-semibold">
          Advisor Coach
        </p>
      </header>

      {/* Tab strip — minimal, scrollable on overflow */}
      <nav
        className="flex items-center gap-0.5 px-1.5 border-b border-slate-100 bg-slate-50/40 overflow-x-auto scrollbar-hide"
        data-testid="advisor-coach-tabs"
        role="tablist"
      >
        {tabs.map((t) => {
          const active = t.key === activeKey;
          return (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={active}
              onClick={() => setActiveKey(t.key)}
              className={`relative inline-flex items-center gap-1.5 px-3.5 py-3 text-[14px] tracking-wide whitespace-nowrap transition-colors ${
                active
                  ? "text-slate-900 font-semibold"
                  : "text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`coach-tab-${t.key}`}
            >
              <span className={active ? "" : "opacity-70"}>{t.icon}</span>
              <span>{t.label}</span>
              {active && (
                <span className="absolute left-2 right-2 -bottom-px h-[2px] bg-teal-600 rounded-full" />
              )}
            </button>
          );
        })}
      </nav>

      <div className="px-4 py-4 min-h-[120px]" data-testid={`coach-body-${activeKey}`}>
        {activeKey === "say" && ask && (
          <p className="text-[17px] text-slate-900 leading-relaxed italic">"{ask}"</p>
        )}

        {activeKey === "why" && (
          <div className="space-y-3.5">
            {why && (
              <p className="text-[16px] text-slate-800 leading-relaxed">{why}</p>
            )}
            {processExplanation && (
              <p className="text-[16px] text-slate-700 leading-relaxed italic pl-3 border-l-2 border-slate-300">
                {processExplanation}
              </p>
            )}
          </div>
        )}

        {activeKey === "expect" && (
          <div className="space-y-3.5">
            {expectationQ && (
              <p className="text-[16px] text-amber-900 leading-relaxed">
                {expectationQ}
              </p>
            )}
            {expectationGuidance && (
              <p className="text-[16px] text-amber-900 leading-relaxed italic pl-3 border-l-2 border-amber-400">
                {expectationGuidance}
              </p>
            )}
          </div>
        )}

        {activeKey === "next" && (
          <div className="space-y-3.5">
            {riskLabels.length > 0 && (
              <ul className="space-y-2" data-testid="coach-risks">
                {riskLabels.map((r) => (
                  <li
                    key={r}
                    className="text-[15.5px] text-amber-900 font-medium leading-snug pl-3 border-l-2 border-amber-500 bg-amber-50 rounded-r py-2"
                  >
                    {r}
                  </li>
                ))}
              </ul>
            )}
            {tagLabels.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="coach-tag-chips">
                {tagLabels.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center text-[14px] font-medium px-3 py-1.5 rounded-full bg-teal-100 border border-teal-300 text-teal-900"
                  >
                    <Wrench className="w-3.5 h-3.5 mr-1.5 opacity-70" />
                    {t}
                  </span>
                ))}
              </div>
            )}
            {likelyOutcomes.length > 0 && (
              <ul className="space-y-2">
                {likelyOutcomes.map((o) => (
                  <li
                    key={o}
                    className="text-[15.5px] text-slate-700 leading-snug pl-3 border-l-2 border-slate-300"
                  >
                    {o}
                  </li>
                ))}
              </ul>
            )}
            {likelyNextSteps.length > 0 && (
              <ul className="space-y-2">
                {likelyNextSteps.map((s) => (
                  <li
                    key={s}
                    className="text-[15.5px] text-slate-700 leading-snug pl-3 border-l-2 border-slate-300"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {activeKey === "safety" && scopeGuidance && (
          <p
            className="text-[16px] text-blue-900 leading-relaxed italic pl-3 border-l-2 border-blue-400"
            data-testid="coach-scope-guidance"
            data-scope-kind={motScopeGuidance ? "mot_only" : "inspection"}
          >
            "{scopeGuidance}"
          </p>
        )}
      </div>
    </section>
  );
}
